import type {
  GoogleBusinessEventMetaDataInput,
  GoogleBusinessOfferMetaDataInput,
  GoogleBusinessPostActionType,
  GoogleBusinessWhatsNewMetaDataInput,
  PostInputMetaData,
} from '../../graphqlTypes'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'
import type { GoogleBusinessChannelData } from '~publish/legacy/composer/composer/entities/Draft/types'
import { transformStringTo24HrTime } from '~publish/legacy/composer/composer/utils/StringTimeTransformer'

import { clearable } from '../shared/clearable'
import { mapGoogleBusinessPostType } from '../shared/postTypes'
import type { MapperContext } from '../../types'
import type { ServiceMapperEntry } from '../types'

const GOOGLE_BUSINESS_POST_ACTION_TYPES: readonly GoogleBusinessPostActionType[] =
  ['none', 'book', 'order', 'shop', 'learn_more', 'signup', 'call']

function isGoogleBusinessPostActionType(
  value: string,
): value is GoogleBusinessPostActionType {
  return GOOGLE_BUSINESS_POST_ACTION_TYPES.some((action) => action === value)
}

// The button select stores its raw value on `cta` without validation, so a
// stale/unrecognized string (e.g. carried over from a future button option)
// falls back to unset rather than being sent as-is. Unset is omitted on
// create, but coalesced to an explicit `'none'` on edit (not a `null` clear):
// the backend's type-switch validation (`mapGoogleBusinessMetadataForEdit`'s
// `validateTypeSwitchPayload`) requires an explicit button, and an unset CTA
// is semantically `'none'` (no call-to-action) — storing `'none'` and
// clearing the field are equivalent for GBP.
function getButton(
  cta: string | null | undefined,
  ctx: MapperContext,
): GoogleBusinessPostActionType | undefined {
  const button =
    cta != null && isGoogleBusinessPostActionType(cta) ? cta : undefined
  return ctx.operation === 'edit' ? (button ?? 'none') : button
}

// Both bar components clear the link field whenever no action button (or
// explicitly "None") is selected, since a link only makes sense alongside a
// button to attach it to (`ChannelDataTransformer.ts` `transformGBPWhatsNew`/
// `transformGBPEvent`). An unrecognized `cta` also counts as "no button" here,
// matching `getButton`'s fallback, so a stale value can't leave a link
// stranded with no button attached.
function getLinkForButton(
  cta: string | null | undefined,
  link: string | null | undefined,
): string | null | undefined {
  const hasButton =
    cta != null && isGoogleBusinessPostActionType(cta) && cta !== 'none'
  return hasButton ? link : undefined
}

// Combines the legacy UTC-midnight date (`start_date`/`end_date`, unix
// seconds) with an optional display time (`start_time`/`end_time`) into a
// single ISO `DateTime`. Full-day events (no time) stay at UTC midnight, so
// the backend's `Math.floor(date.getTime() / 1000)` reproduces the exact unix
// value the date was originally stored as; timed events bake the time-of-day
// into the same instant (see the mapper's final report for the full
// round-trip).
function toIsoDateTime(
  unixSeconds: number,
  time: string | null | undefined,
): string {
  const date = new Date(unixSeconds * 1000)
  if (time) {
    const { hours, minutes } = transformStringTo24HrTime(time)
    date.setUTCHours(hours, minutes, 0, 0)
  }
  return date.toISOString()
}

function mapEventDetails(
  data: GoogleBusinessChannelData | undefined,
  ctx: MapperContext,
): GoogleBusinessEventMetaDataInput {
  const hasTime = !!data?.start_time && !!data?.end_time
  const link = getLinkForButton(data?.cta, data?.link)

  return {
    isFullDayEvent: !hasTime,
    title: clearable(data?.title, ctx),
    startDate: clearable(
      data?.start_date != null
        ? toIsoDateTime(data.start_date, hasTime ? data.start_time : undefined)
        : undefined,
      ctx,
    ),
    endDate: clearable(
      data?.end_date != null
        ? toIsoDateTime(data.end_date, hasTime ? data.end_time : undefined)
        : undefined,
      ctx,
    ),
    button: getButton(data?.cta, ctx),
    link: clearable(link, ctx),
  }
}

function mapOfferDetails(
  data: GoogleBusinessChannelData | undefined,
  ctx: MapperContext,
): GoogleBusinessOfferMetaDataInput {
  // Offers have no action button (`transformGBPOffer` always clears `cta`)
  // and no time picker (`OfferBar` renders `DateOptionalTimePicker` with
  // `hideTime`), so dates never carry a time-of-day here even if a stale
  // `start_time`/`end_time` survives from a previous Event edit.
  return {
    title: clearable(data?.title, ctx),
    startDate: clearable(
      data?.start_date != null
        ? toIsoDateTime(data.start_date, undefined)
        : undefined,
      ctx,
    ),
    endDate: clearable(
      data?.end_date != null
        ? toIsoDateTime(data.end_date, undefined)
        : undefined,
      ctx,
    ),
    code: clearable(data?.code, ctx),
    link: clearable(data?.link, ctx),
    terms: clearable(data?.terms, ctx),
  }
}

function mapWhatsNewDetails(
  data: GoogleBusinessChannelData | undefined,
  ctx: MapperContext,
): GoogleBusinessWhatsNewMetaDataInput {
  const link = getLinkForButton(data?.cta, data?.link)

  return {
    button: getButton(data?.cta, ctx),
    link: clearable(link, ctx),
  }
}

// Google Business Profile: post `type` (`whats_new`/`offer`/`event`) plus
// exactly one details block matching it, per `channelData.googlebusiness`.
// `title` lives only in the details block — the create-path validator
// (`validateGoogleBusinessCreateMetadata`) reads the nested `title`, not the
// sibling top-level `PostInputMetaData.google.title`, so populating only the
// top-level field would fail create for Event/Offer. There is no location or
// subprofile concept for GBP (unlike Pinterest's board).
//
// `ctx.channelVariant` (when present) supplies `channelData`/`updateType`;
// the branching detail mappers above are pure functions of those, so nothing
// else in this file needs to change.
export function mapGoogleBusiness(
  draft: Draft,
  ctx: MapperContext,
): PostInputMetaData {
  const variant = ctx.channelVariant
  const data = variant
    ? variant.channelData?.googlebusiness
    : draft.channelData?.googlebusiness
  const updateType = variant ? variant.updateType : draft.updateType
  const type = mapGoogleBusinessPostType(updateType)

  if (type === 'event') {
    return { google: { type, detailsEvent: mapEventDetails(data, ctx) } }
  }
  if (type === 'offer') {
    return { google: { type, detailsOffer: mapOfferDetails(data, ctx) } }
  }
  return { google: { type, detailsWhatsNew: mapWhatsNewDetails(data, ctx) } }
}

export const googlebusiness: ServiceMapperEntry = {
  mapper: mapGoogleBusiness,
}
